// Image uploads — multipart POST to Go backend.
// Paths stored are relative: "<slug>/<filename>" — resolved to "/uploads/<path>" for display.

import { auth } from "./init.js";
import { API_BASE } from "./config.js";

async function idToken() {
  if (!auth?.currentUser) return null;
  return auth.currentUser.getIdToken();
}

// Upload a single image file. Returns { path, url }.
export async function uploadImagePage(slug, file) {
  const token = await idToken();
  if (!token) throw new Error("Not signed in");

  const form = new FormData();
  form.append("image", file);

  const res = await fetch(`${API_BASE}/events/${encodeURIComponent(slug)}/images`, {
    method: "POST",
    headers: { Authorization: "Bearer " + token },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Upload failed: ${res.status} ${text}`);
  }
  return res.json(); // { path: "slug/filename", url: "/uploads/slug/filename" }
}

// Upload a hero image for a wizard-built invite. Returns { path, url }.
export async function uploadHeroImage(slug, file) {
  const token = await idToken();
  if (!token) throw new Error("Not signed in");

  const form = new FormData();
  form.append("image", file);

  const res = await fetch(`${API_BASE}/events/${encodeURIComponent(slug)}/hero-image`, {
    method: "POST",
    headers: { Authorization: "Bearer " + token },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Upload failed: ${res.status} ${text}`);
  }
  return res.json();
}

// Upload a custom background image/video for a wizard-built invite.
// Reuses the event asset storage path; the caller stores the returned path in theme.
export async function uploadBackgroundMedia(slug, file) {
  const token = await idToken();
  if (!token) throw new Error("Not signed in");

  const form = new FormData();
  form.append("image", file);

  const res = await fetch(`${API_BASE}/events/${encodeURIComponent(slug)}/hero-image`, {
    method: "POST",
    headers: { Authorization: "Bearer " + token },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Upload failed: ${res.status} ${text}`);
  }
  return res.json();
}

export async function uploadSocialCard(slug, blob) {
  const token = await idToken();
  if (!token) throw new Error("Not signed in");

  const form = new FormData();
  form.append("image", blob, "social-card.png");

  const res = await fetch(`${API_BASE}/events/${encodeURIComponent(slug)}/social-card`, {
    method: "POST",
    headers: { Authorization: "Bearer " + token },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Upload failed: ${res.status} ${text}`);
  }
  return res.json();
}

export async function uploadRegistrationImage(slug, file) {
  const form = new FormData();
  form.append("image", file);

  const res = await fetch(`${API_BASE}/events/${encodeURIComponent(slug)}/registration-uploads`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Upload failed: ${res.status} ${text}`);
  }
  return res.json();
}

// Resolve a stored path to a display URL.
export function urlFor(path) {
  return "/uploads/" + path;
}

// Delete an uploaded image page.
export async function removeImagePage(path) {
  const token = await idToken();
  if (!token) throw new Error("Not signed in");

  // New paths are "<uid>/<slug>/<filename>"; legacy paths are "<slug>/<filename>".
  const parts = path.split("/");
  const slug = parts.length >= 3 ? parts[1] : parts[0];

  await fetch(`${API_BASE}/events/${encodeURIComponent(slug)}/images/${encodeURIComponent(path)}`, {
    method: "DELETE",
    headers: { Authorization: "Bearer " + token },
  });
}

// Delete an uploaded hero image or other event asset.
export async function removeHeroImage(path) {
  const token = await idToken();
  if (!token) throw new Error("Not signed in");

  await fetch(`${API_BASE}/events/assets/${encodeURIComponent(path)}`, {
    method: "DELETE",
    headers: { Authorization: "Bearer " + token },
  });
}

// Resolve an ordered list of paths to display URLs.
export function resolvePageUrls(paths = []) {
  return Promise.resolve(paths.map(urlFor));
}
